import { useState, useRef, useMemo } from "react";
import {
  Plus, Pencil, Trash, Search, Eye, ArrowUp, ArrowDown, X, CalendarDays, FolderInput, FolderPlus,
} from "lucide-react";
import { usePrescriptionTemplates, usePrescriptionTemplateGroups, useDrugs, type PrescriptionTemplate, type PrescriptionTemplateGroup, type MedicineRow } from "@/lib/queries";
import {
  useCreatePrescriptionTemplate, useUpdatePrescriptionTemplate, useDeletePrescriptionTemplate,
  useCreatePrescriptionTemplateGroup, useUpdatePrescriptionTemplateGroup, useDeletePrescriptionTemplateGroup,
  useMovePrescriptionTemplate,
} from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { bucketTemplates, UNGROUPED_LABEL } from "@/lib/templateGroups";
import { EditMedicineDialog } from "@/features/emr/EditMedicineDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FrequencyInput } from "@/features/emr/FrequencyInput";
import {
  type DraftMedicine, type DraftTaperRow, TAPER_GRID, TAPER_GRID_DRUG,
  addDays, stepToDate, rechainTaperRows, newTaperRow, draftToTaperSteps,
} from "@/features/emr/taperDraft";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { TemplateMedicine, TaperStep } from "@emr/shared";
import { isValidFrequency, normalizeFrequency } from "@emr/shared";
import { fmtDate, dateIST } from "@emr/shared/india";
import { toast } from "sonner";

/**
 * Prescription Templates management page (/prescription-templates).
 *
 * A shared clinic-wide library of named medicine-row bundles. Templates are
 * inserted into a visit's prescription from the EMR visit page's
 * "Templates" button; this page is where they are created, viewed, edited,
 * and deleted.
 *
 * Drug entry mirrors the EMR visit page's prescription flow exactly: a
 * search box (3+ chars → live /drugs?q= dropdown + "Add as custom drug")
 * opens an inline draft panel (frequency / days / instructions, or the
 * taper protocol grid) — no separate "Add medicine" button.
 *
 * The listing is intentionally slim: template name + view / edit / delete
 * buttons. Clicking a template (or its View button) opens a VIEW dialog
 * showing its medicines; the view dialog has an Edit button that switches
 * the same dialog into edit mode (name + rows) and saves from there.
 */

// Template medicines are stored WITHOUT row metadata (id/prescriptionId/
// serialNo). The editor dialogs work with MedicineRow, so we bridge: a
// synthetic id/prescriptionId is attached for editing and stripped on save.
// MedicineRow.formulation is a loose string (the DB enum arrives as text);
// TemplateMedicine narrows it to the Formulation union — cast on the way in.
function toMedicineRow(m: TemplateMedicine, idx: number): MedicineRow {
  return {
    id: crypto.randomUUID(),
    prescriptionId: "template",
    drugId: m.drugId,
    serialNo: idx + 1,
    name: m.name,
    composition: m.composition,
    formulation: m.formulation,
    strength: m.strength,
    frequency: m.frequency,
    taper: m.taper,
    duration: m.duration,
    instructions: m.instructions,
  };
}

function toTemplateMedicine(m: MedicineRow): TemplateMedicine {
  return {
    drugId: m.drugId,
    name: m.name,
    composition: m.composition,
    formulation: m.formulation as TemplateMedicine["formulation"],
    strength: m.strength,
    frequency: m.frequency,
    taper: m.taper,
    duration: m.duration,
    instructions: m.instructions,
  };
}

// ---- Draft state ----
// DraftTaperRow / DraftMedicine, the grid templates and the date + rechain
// helpers now live in @/features/emr/taperDraft (shared with PrescriptionTable
// and EditMedicineDialog, Batch 7 Wave 3). This page's per-step drug box
// stays a plain <Input> (no /drugs search dropdown) — its original behaviour.

/**
 * Rows editor used in the template dialog's EDIT mode: EMR-style drug
 * search + inline draft panel + rows table + per-row EditMedicineDialog.
 * Self-contained — owns the search, draft, and editing state.
 */
function TemplateRowsEditor({ rows, setRows, move, removeRow }: {
  rows: MedicineRow[];
  setRows: React.Dispatch<React.SetStateAction<MedicineRow[]>>;
  move: (idx: number, dir: -1 | 1) => void;
  removeRow: (id: string) => void;
}) {
  // ---- Drug search (same flow as the EMR prescription table) ----
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebouncedValue(query, 250);
  const { data: drugsData, isLoading: drugsLoading } = useDrugs(debouncedQuery.length >= 3 ? debouncedQuery : "");
  const drugs = (drugsData && "rows" in drugsData ? drugsData.rows : Array.isArray(drugsData) ? drugsData : []) as { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }[];
  const drugResults = drugs.slice(0, 10);
  const drugListCount = drugResults.length + 1; // +1 custom-drug entry

  // ---- Draft panel (mirrors PrescriptionTable's draft state) ----
  const [draft, setDraft] = useState<DraftMedicine | null>(null);

  // ---- Per-row edit dialog (same dialog the EMR table uses) ----
  const [editing, setEditing] = useState<MedicineRow | null>(null);

  const { highlight: drugHighlight, highlightRef: drugHighlightRef, onKeyDown: onDrugSearchKeyDown } = useDropdownNav({
    count: showDropdown && query.length >= 3 ? drugListCount : 0,
    onSelect: (i) => {
      if (i < drugResults.length) startAdd(drugResults[i]);
      else startAdd(undefined);
    },
    inputRef: searchRef,
    onEscape: () => setShowDropdown(false),
  });

  const startAdd = (drug?: { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }) => {
    setDraft({
      drugId: drug?.id ?? null,
      name: drug?.name ?? query,
      composition: drug?.composition ?? null,
      formulation: (drug?.formulation as MedicineRow["formulation"]) ?? "Tablet",
      strength: drug?.strength ?? "",
      frequency: "",
      taper: null,
      days: "",
      instructions: "",
      perStrength: false,
    });
    setShowDropdown(false);
  };

  const enableTaper = () => {
    if (!draft) return;
    setDraft({ ...draft, taper: draft.taper ?? [newTaperRow(dateIST(), draft, draft.perStrength)] });
  };
  const disableTaper = () => {
    if (!draft) return;
    setDraft({ ...draft, taper: null });
  };
  const togglePerStrength = (checked: boolean) => {
    if (!draft) return;
    if (!checked) {
      setDraft({
        ...draft,
        perStrength: false,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: "", strength: "", composition: null, formulation: null, drugId: null })),
      });
    } else {
      setDraft({
        ...draft,
        perStrength: true,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: r.name || draft.name, strength: r.strength || draft.strength, composition: r.composition ?? draft.composition, formulation: r.formulation ?? draft.formulation, drugId: r.drugId ?? draft.drugId })),
      });
    }
  };
  // Edit one taper row. A from-date or days change re-chains every FOLLOWING
  // row's from-date (prev To + 1) so start/end stay continuous — the same
  // rule the "+" append uses (mirrors the EMR dialog, 2026-10-30).
  const setTaperRow = (key: string, patch: Partial<DraftTaperRow>) => {
    if (!draft?.taper) return;
    const idx = draft.taper.findIndex((r) => r.key === key);
    if (idx < 0) return;
    const patched = draft.taper.map((r) => (r.key === key ? { ...r, ...patch } : r));
    setDraft({ ...draft, taper: "fromDate" in patch || "days" in patch ? rechainTaperRows(patched, idx) : patched });
  };
  const removeTaperRow = (key: string) => {
    if (!draft?.taper) return;
    setDraft({ ...draft, taper: draft.taper.filter((r) => r.key !== key) });
  };
  const addTaperRow = () => {
    if (!draft?.taper) return;
    const last = draft.taper[draft.taper.length - 1];
    let fromDate = dateIST();
    if (last) {
      const to = stepToDate(last.fromDate, last.days);
      if (to) fromDate = addDays(to, 1);
    }
    const inherit = draft.perStrength && last
      ? { name: last.name || draft.name, strength: last.strength || draft.strength, composition: last.composition ?? draft.composition, formulation: last.formulation ?? draft.formulation, drugId: last.drugId ?? draft.drugId }
      : draft;
    setDraft({ ...draft, taper: [...draft.taper, newTaperRow(fromDate, inherit as DraftMedicine, draft.perStrength)] });
  };

  const confirmAdd = () => {
    if (!draft || !draft.name.trim()) return;
    const freq = normalizeFrequency(draft.frequency);
    if (draft.taper) {
      for (const [i, r] of draft.taper.entries()) {
        if (!r.fromDate) { toast.error(`Protocol row ${i + 1}: pick a From date`); return; }
        if (!r.days || parseInt(r.days, 10) < 1) { toast.error(`Protocol row ${i + 1}: enter the number of days`); return; }
        if (!normalizeFrequency(r.frequency) || !isValidFrequency(normalizeFrequency(r.frequency))) {
          toast.error(`Protocol row ${i + 1}: enter a frequency like 1-1-1-1`);
          return;
        }
        if (draft.perStrength && !r.name.trim()) {
          toast.error(`Protocol row ${i + 1}: pick a drug for this row`);
          return;
        }
      }
      if (draft.taper.length === 0) { toast.error("Add at least one protocol row"); return; }
    } else {
      if (!freq && !draft.instructions.trim()) {
        toast.error("Enter a frequency or instructions");
        return;
      }
      if (!isValidFrequency(freq)) {
        toast.error("Frequency looks invalid — use numbers like 1-1-1 or 1.5-0-1");
        return;
      }
    }
    const taperSteps: TaperStep[] | null = draftToTaperSteps(draft);
    const newRow: MedicineRow = {
      id: crypto.randomUUID(),
      prescriptionId: "template",
      drugId: draft.drugId,
      serialNo: rows.length + 1,
      name: draft.name,
      composition: draft.composition,
      formulation: draft.formulation,
      strength: draft.strength || null,
      frequency: taperSteps ? null : (freq || null),
      taper: taperSteps,
      duration: taperSteps ? null : (draft.days ? `${draft.days} days` : null),
      instructions: draft.instructions || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setRows([...rows, newRow]);
    setDraft(null);
    setQuery("");
    // Return cursor to the search box for the next drug (same as EMR).
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  return (
    <div className="space-y-3">
      {/* Drug search — same flow as the EMR prescription table. */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input
          ref={searchRef}
          placeholder="Search drug name or composition (3+ chars)…"
          className="pl-9"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(e.target.value.length >= 3); }}
          onFocus={() => query.length >= 3 && setShowDropdown(true)}
          onKeyDown={onDrugSearchKeyDown}
        />
        {showDropdown && query.length >= 3 && (
          <div className="visible-scrollbar absolute z-20 mt-1 max-h-60 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
            {drugsLoading ? (
              <div className="p-2"><Skeleton className="h-6" /></div>
            ) : drugs.length === 0 ? (
              <button
                ref={drugHighlight === 0 ? drugHighlightRef : undefined}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                  drugHighlight === 0 ? "bg-cream-200 dark:bg-dark-700" : ""
                }`}
                onClick={() => startAdd(undefined)}
              >
                <Plus size={14} className="text-maroon-600" /> Add "{query}" as custom drug
              </button>
            ) : (
              <>
                {drugResults.map((d, idx) => (
                  <button
                    key={d.id}
                    ref={idx === drugHighlight ? drugHighlightRef : undefined}
                    className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                      idx === drugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                    }`}
                    onClick={() => startAdd(d)}
                  >
                    <span className="font-medium text-ink dark:text-cream-50">{d.name}</span>
                    <span className="text-xs text-muted">
                      {[d.composition, d.formulation, d.strength].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))}
                <button
                  ref={drugResults.length === drugHighlight ? drugHighlightRef : undefined}
                  className={`flex w-full items-center gap-2 border-t border-cream-200 px-3 py-2 text-left text-sm text-maroon-600 hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700 ${
                    drugResults.length === drugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                  }`}
                  onClick={() => startAdd(undefined)}
                >
                  <Plus size={14} /> Add "{query}" as custom drug
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Inline draft panel — same layout as the EMR add-drug draft. */}
      {draft && (
        <div className="rounded-lg border border-maroon-200 bg-cream-50/50 p-3 dark:border-amber-700/40 dark:bg-dark-800/50">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium">
              {draft.name}{draft.strength ? ` — ${draft.strength}` : ""}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
          </div>

          <div className="mb-2 flex items-center gap-2">
            <Checkbox
              id="tpl-taper-toggle"
              checked={!!draft.taper}
              onCheckedChange={(checked) => (checked ? enableTaper() : disableTaper())}
            />
            <Label htmlFor="tpl-taper-toggle" className="text-xs cursor-pointer">
              Escalation / De-escalation protocol
            </Label>
            {draft.taper && (
              <>
                <Checkbox
                  id="tpl-per-strength-toggle"
                  className="ml-4"
                  checked={draft.perStrength}
                  onCheckedChange={(checked) => togglePerStrength(checked === true)}
                />
                <Label htmlFor="tpl-per-strength-toggle" className="text-xs cursor-pointer">
                  Allow different strengths per row
                </Label>
              </>
            )}
          </div>

          {draft.taper ? (
            <div className="space-y-1.5">
              <div className={`${draft.perStrength ? TAPER_GRID_DRUG : TAPER_GRID} text-xs text-muted`}>
                {draft.perStrength && <span>Drug</span>}
                <span>From date</span>
                <span>Frequency</span>
                <span>Days</span>
                <span>To date</span>
                <span>Instructions</span>
                <span className="text-center">+</span>
                <span className="text-center">×</span>
              </div>
              {draft.taper.map((r) => {
                const to = stepToDate(r.fromDate, r.days);
                return (
                  <div key={r.key} className={draft.perStrength ? TAPER_GRID_DRUG : TAPER_GRID}>
                    {draft.perStrength && (
                      <Input
                        className="h-8 text-xs"
                        value={r.name}
                        onChange={(e) => setTaperRow(r.key, { name: e.target.value })}
                        placeholder="Drug / strength"
                      />
                    )}
                    <Input
                      type="date"
                      className="h-8"
                      value={r.fromDate}
                      onChange={(e) => setTaperRow(r.key, { fromDate: e.target.value })}
                    />
                    <FrequencyInput
                      className="h-8"
                      value={r.frequency}
                      onChange={(v) => setTaperRow(r.key, { frequency: v })}
                    />
                    <Input
                      type="number"
                      min={1}
                      className="h-8"
                      value={r.days}
                      onChange={(e) => setTaperRow(r.key, { days: e.target.value })}
                    />
                    <div className="flex h-8 items-center gap-1 text-sm text-muted">
                      <CalendarDays size={13} className="shrink-0" />
                      <span className="whitespace-nowrap">{to ? fmtDate(to) : "—"}</span>
                    </div>
                    <Input
                      className="h-8"
                      placeholder="With breakfast"
                      value={r.instructions}
                      onChange={(e) => setTaperRow(r.key, { instructions: e.target.value })}
                    />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-maroon-600" onClick={addTaperRow} title="Add next row">
                      <Plus size={14} />
                    </Button>
                    <Button
                      size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                      onClick={() => removeTaperRow(r.key)}
                      disabled={draft.taper!.length === 1}
                      title="Remove row"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                );
              })}
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <Plus size={12} className="text-maroon-600" />
                {draft.perStrength
                  ? <>Use <b>+</b> to append a row. Enter the drug/strength in each row's Drug box; consecutive same-strength rows print under one header.</>
                  : <>Use <b>+</b> on a row to append the next period (its From date starts the day after the previous To date).</>}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[180px_110px_1fr_auto] items-end gap-2">
              <div>
                <Label className="text-xs">Frequency</Label>
                <FrequencyInput
                  value={draft.frequency}
                  onChange={(v) => setDraft({ ...draft, frequency: v })}
                  onCommit={confirmAdd}
                />
              </div>
              <div>
                <Label className="text-xs">Days</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.days}
                  onChange={(e) => setDraft({ ...draft, days: e.target.value })}
                  placeholder="5"
                />
              </div>
              <div>
                <Label className="text-xs">Instructions</Label>
                <Input
                  value={draft.instructions}
                  onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
                  placeholder="e.g. After meals (optional)"
                />
              </div>
              <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={confirmAdd}>
                <Plus size={14} className="mr-1" /> Add to template
              </Button>
            </div>
          )}

          {draft.taper && (
            <div className="mt-2 flex justify-end">
              <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={confirmAdd}>
                <Plus size={14} className="mr-1" /> Add to template
              </Button>
              <span className="ml-3 self-center text-xs text-muted">
                Days and frequency come from the protocol rows above.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Rows table — same columns as the EMR prescription table. */}
      {rows.length === 0 ? (
        <div className="rounded-md bg-cream-50 p-6 text-center text-sm text-muted dark:bg-dark-800">
          No medicines yet — search above to add drugs.
        </div>
      ) : (
        <div className="max-h-[40vh] overflow-auto rounded-md border border-cream-200 dark:border-dark-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead className="w-12">Sr #</TableHead>
                <TableHead>Drug</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead className="w-28">Frequency</TableHead>
                <TableHead className="w-24">Days</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m, idx) => (
                <TableRow key={m.id} className="even:bg-cream-50/50">
                  <TableCell className="font-mono text-xs">{idx + 1}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => setEditing(m)}>
                    <div className="font-medium text-ink dark:text-cream-50">{m.name}</div>
                    {m.composition && <div className="text-xs text-muted">{m.composition}</div>}
                    {m.taper && (
                      <div className="text-xs text-maroon-600 dark:text-amber-500">
                        Escalation / De-escalation protocol — {m.taper.length} period(s)
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{m.instructions ?? "—"}</TableCell>
                  <TableCell className="break-all font-mono text-xs">{m.taper ? "—" : (m.frequency ?? "—")}</TableCell>
                  <TableCell>
                    {m.taper ? (
                      <Badge variant="outline" className="border-maroon-300 text-maroon-700 dark:border-amber-600 dark:text-amber-500">Taper</Badge>
                    ) : (
                      <span className="text-xs">{m.duration ?? "—"}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={() => move(idx, -1)}>
                        <ArrowUp size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === rows.length - 1} onClick={() => move(idx, 1)}>
                        <ArrowDown size={13} />
                      </Button>
                      {/* Edit — same dialog the EMR table uses (pencil button,
                          matching the EMR visit page's per-row Edit). */}
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditing(m)} title="Edit medicine">
                        <Pencil size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => removeRow(m.id)}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Row editor — the same dialog the EMR prescription table uses. */}
      <EditMedicineDialog
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        medicine={editing}
        onSave={(m) => {
          if (!editing) return;
          setRows(rows.map((r) => (r.id === editing.id ? { ...r, ...m } : r)));
        }}
        onDelete={() => { if (editing) removeRow(editing.id); }}
      />
    </div>
  );
}

// ---- The template dialog: VIEW mode (read-only) or EDIT mode (name + rows) ----

function TemplateDialog({ template, initialMode, onOpenChange, create, update, id, groups }: {
  template: PrescriptionTemplate | null; // null = create-new
  initialMode?: "view" | "edit" | null; // explicit open mode (Edit button → edit)
  onOpenChange: (v: boolean) => void;
  create: ReturnType<typeof useCreatePrescriptionTemplate>;
  update: ReturnType<typeof useUpdatePrescriptionTemplate>;
  id?: string;
  groups: PrescriptionTemplateGroup[];
}) {
  const isCreate = !template;
  const [mode, setMode] = useState<"view" | "edit">(initialMode ?? (isCreate ? "edit" : "view"));
  const [name, setName] = useState(template?.name ?? "");
  // "__none__" = no group selected (used only in create mode; existing
  // templates always carry a definite groupId-or-null).
  const NONE = "__none__";
  const [groupId, setGroupId] = useState<string>(template?.groupId ?? NONE);
  const [rows, setRows] = useState<MedicineRow[]>(
    (template?.medicines ?? []).map((m, i) => toMedicineRow(m, i)),
  );

  const submit = async () => {
    if (!name.trim()) { toast.error("Enter a template name"); return; }
    if (rows.length === 0) { toast.error("Add at least one medicine"); return; }
    const medicines = rows.map(toTemplateMedicine);
    try {
      if (template && id) {
        await update.mutateAsync({
          name: name.trim(), medicines,
          // Only send groupId when the user picked one (or moved an
          // existing template back to Ungrouped); never on a plain
          // name/rows edit of an ungrouped template.
          ...(groupId !== template.groupId ? { groupId: groupId === NONE ? null : groupId } : {}),
          updatedAt: template.updatedAt,
        });
      } else {
        await create.mutateAsync({ name: name.trim(), medicines, ...(groupId !== NONE ? { groupId } : {}) });
      }
      onOpenChange(false);
    } catch { /* onError toast already fired */ }
  };

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[idx], next[target]] = [next[target], next[idx]];
    setRows(next.map((r, i) => ({ ...r, serialNo: i + 1 })));
  };

  const removeRow = (rowId: string) => {
    setRows(rows.filter((r) => r.id !== rowId).map((r, i) => ({ ...r, serialNo: i + 1 })));
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className={mode === "view" ? "max-w-3xl" : "max-w-4xl"}>
        <DialogHeader>
          <DialogTitle>
            {mode === "view"
              ? template?.name
              : isCreate ? "Add template" : `Edit template — ${template?.name}`}
          </DialogTitle>
        </DialogHeader>

        {mode === "view" ? (
          <>
            <div className="max-h-[60vh] overflow-auto">
              {rows.length === 0 ? (
                <div className="rounded-md bg-cream-50 p-6 text-center text-sm text-muted dark:bg-dark-800">
                  This template has no medicines.
                </div>
              ) : (
                <ul className="space-y-2">
                  {rows.map((m, i) => (
                    <li key={m.id} className="rounded-md bg-cream-50 p-2.5 text-sm dark:bg-dark-800">
                      <div className="font-medium text-ink dark:text-cream-50">
                        {i + 1}. {m.name}{m.strength ? ` — ${m.strength}` : ""}
                      </div>
                      {m.composition && <div className="text-xs text-muted">{m.composition}</div>}
                      <div className="mt-0.5 text-xs text-muted">
                        {m.taper
                          ? [`Escalation / De-escalation protocol (${m.taper.length} periods)`, m.taper.map((s) => `${s.frequency}×${s.days}d`).join(", ")].join(" · ")
                          : [m.frequency, m.duration, m.instructions].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              <Button
                onClick={() => setMode("edit")}
                className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
              >
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label>Template name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Migraine prophylaxis" />
              </div>
              <div>
                <Label>Group</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="No group (Ungrouped)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No group (Ungrouped)</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <TemplateRowsEditor rows={rows} setRows={setRows} move={move} removeRow={removeRow} />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { if (isCreate) onOpenChange(false); else setMode("view"); }}>
                {isCreate ? "Cancel" : "Back"}
              </Button>
              <Button onClick={submit} disabled={!name.trim() || rows.length === 0 || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
                Save
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PrescriptionTemplatesPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("prescriptions.create");
  const canEdit = isAdmin || permissions.has("prescriptions.edit");
  const canDelete = isAdmin || permissions.has("prescriptions.delete");
  const { data, isLoading } = usePrescriptionTemplates();
  const { data: groupsData, isLoading: groupsLoading } = usePrescriptionTemplateGroups();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTpl, setActiveTpl] = useState<PrescriptionTemplate | null>(null);
  // Bumped on every open so the dialog remounts with fresh state (name/rows
  // initialize from the template; a stale prior edit can't leak through).
  const [openSeq, setOpenSeq] = useState(0);
  const create = useCreatePrescriptionTemplate();
  const update = useUpdatePrescriptionTemplate(activeTpl?.id ?? "");
  const del = useDeletePrescriptionTemplate();
  const move = useMovePrescriptionTemplate();
  // Stable refs for useMemo deps (avoids the exhaustive-deps warning from
  // `?? []` creating a fresh array each render).
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const groups = useMemo(() => groupsData?.rows ?? [], [groupsData]);
  const q = search.trim().toLowerCase();
  // Stable filtered list (useMemo so the buckets dep stays stable).
  const filtered = useMemo(() => q
    ? rows.filter((t) =>
        t.name.toLowerCase().includes(q) ||
        t.medicines.some((m) => m.name.toLowerCase().includes(q)))
    : rows, [q, rows]);
  // Accordion expansion (null = untouched → only the Ungrouped section
  // open; named groups start COLLAPSED — user request 2026-11-02).
  // Controlled so a search-clear doesn't collapse sections the user
  // opened/closed by hand.
  const [expanded, setExpanded] = useState<string[] | null>(null);
  const [forceMode, setForceMode] = useState<"view" | "edit" | null>(null);
  // Group buckets over the FILTERED list — the picker's post-#295 pattern
  // (was `q ? [] : bucketTemplates(filtered, groups)`, which rendered an
  // EMPTY accordion while searching: matches existed in `filtered` but no
  // group ever displayed them — the "flat list" the old comment promised
  // was never implemented). Searching keeps the accordion structure: only
  // groups holding a match render (empty buckets are dropped by
  // bucketTemplates), each at its current fold. Empty groups are
  // inspectable in the Groups tab instead.
  const buckets = useMemo(
    () => bucketTemplates(filtered, groups),
    [filtered, groups],
  );

  const openDialog = (tpl: PrescriptionTemplate | null, mode?: "view" | "edit") => {
    // The dialog defaults to view for existing templates and edit for new;
    // the explicit mode is used when opened via the Edit button.
    setActiveTpl(tpl);
    setOpenSeq((n) => n + 1);
    setForceMode(mode ?? null);
    setDialogOpen(true);
  };

  // Move a template into a group (or Ungrouped) via the row's ⋯ menu.
  const moveTo = (tpl: PrescriptionTemplate, groupId: string | null) => {
    if (groupId === tpl.groupId) return; // already there — no-op
    move.mutate({ id: tpl.id, groupId });
  };

  // ---- Groups tab state ----
  const createGroup = useCreatePrescriptionTemplateGroup();
  const updateGroup = useUpdatePrescriptionTemplateGroup(); // id passed per call
  const deleteGroup = useDeletePrescriptionTemplateGroup();
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupEdit, setGroupEdit] = useState<PrescriptionTemplateGroup | null>(null);
  const [groupName, setGroupName] = useState("");

  const openGroupDialog = (g: PrescriptionTemplateGroup | null) => {
    setGroupEdit(g);
    setGroupName(g?.name ?? "");
    setGroupDialogOpen(true);
  };
  const submitGroup = async () => {
    const name = groupName.trim();
    if (!name) return;
    try {
      if (groupEdit) await updateGroup.mutateAsync({ id: groupEdit.id, name, updatedAt: groupEdit.updatedAt });
      else await createGroup.mutateAsync({ name });
      setGroupDialogOpen(false);
    } catch { /* onError toast already fired */ }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Prescription Templates</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <Input
              className="pl-9 w-56"
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canCreate && (
            <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => openDialog(null, "edit")}>
              <Plus size={16} className="mr-1" /> Add Template
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-2">
      {isLoading || groupsLoading ? <Skeleton className="h-40" /> :
       filtered.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted">
            {rows.length === 0 ? "No prescription templates yet." : "No templates match your search."}
          </CardContent>
        </Card>
      ) : (
        // Grouped accordions — named groups start COLLAPSED, only
        // "Ungrouped" starts open (a7e33b6); folds persist across
        // search/data changes (controlled), and while searching only
        // groups holding a match render (see the buckets memo above).
        <Accordion
          type="multiple"
          value={expanded ?? buckets.filter((b) => b.groupId === null).map((b) => b.key)}
          onValueChange={setExpanded}
          className="space-y-2"
        >
          {buckets.map((b) => (
            <AccordionItem key={b.key} value={b.key} className="border border-cream-200 rounded-lg dark:border-dark-700">
              <AccordionTrigger className="px-4 py-3 font-serif text-base text-maroon-700 hover:no-underline dark:text-amber-500">
                <span className="flex items-center gap-2">
                  {b.name}
                  <span className="font-sans text-xs font-normal text-muted">
                    {b.items.length} template{b.items.length === 1 ? "" : "s"}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2 px-3 pb-3">
                {b.items.map((t) => (
                  <Card key={t.id} className="border-cream-200 dark:border-dark-700">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                      <CardTitle
                        className="cursor-pointer font-serif text-base text-maroon-700 hover:underline dark:text-amber-500"
                        onClick={() => openDialog(t, "view")}
                        title="View template"
                      >
                        {t.name}
                      </CardTitle>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="View" onClick={() => openDialog(t, "view")}>
                          <Eye size={14} />
                        </Button>
                        {canEdit && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" onClick={() => openDialog(t, "edit")}>
                            <Pencil size={14} />
                          </Button>
                        )}
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Move to group">
                                <FolderInput size={14} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Move to group</DropdownMenuLabel>
                              {groups.map((g) => (
                                <DropdownMenuItem
                                  key={g.id}
                                  disabled={g.id === t.groupId}
                                  onClick={() => moveTo(t, g.id)}
                                >
                                  {g.name}
                                  {g.id === t.groupId ? <span className="ml-auto text-xs text-muted">current</span> : null}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={t.groupId === null}
                                onClick={() => moveTo(t, null)}
                              >
                                {UNGROUPED_LABEL}
                                {t.groupId === null ? <span className="ml-auto text-xs text-muted">current</span> : null}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {canDelete && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500"
                            title="Delete"
                            onClick={() => {
                              if (window.confirm(`Delete template "${t.name}"?`)) del.mutate(t.id);
                            }}
                          >
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
        </TabsContent>

        <TabsContent value="groups" className="space-y-2">
          <div className="flex items-center justify-end">
            {canCreate && (
              <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => openGroupDialog(null)}>
                <FolderPlus size={14} className="mr-1" /> Add Group
              </Button>
            )}
          </div>
          {groupsLoading ? <Skeleton className="h-32" /> :
           groups.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted">
                No groups yet — create one to organize your templates.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {groups.map((g) => {
                const count = rows.filter((t) => t.groupId === g.id).length;
                return (
                  <Card key={g.id}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                      <CardTitle className="font-serif text-base text-maroon-700 dark:text-amber-500">
                        {g.name}
                        <span className="ml-2 font-sans text-xs font-normal text-muted">
                          {count} template{count === 1 ? "" : "s"}
                        </span>
                      </CardTitle>
                      <div className="flex gap-1">
                        {canEdit && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Rename group" onClick={() => openGroupDialog(g)}>
                            <Pencil size={14} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500"
                            title="Delete group"
                            onClick={() => {
                              if (window.confirm(`Delete group "${g.name}"? Its templates move to "Ungrouped" — they are NOT deleted.`)) {
                                deleteGroup.mutate(g.id);
                              }
                            }}
                          >
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {dialogOpen && (
        <TemplateDialog
          key={`${activeTpl?.id ?? "new"}-${openSeq}`}
          template={activeTpl}
          initialMode={forceMode}
          onOpenChange={(v) => { if (!v) { setDialogOpen(false); setForceMode(null); } }}
          create={create}
          update={update}
          id={activeTpl?.id}
          groups={groups}
        />
      )}

      <Dialog open={groupDialogOpen} onOpenChange={(v) => { if (!v) { setGroupDialogOpen(false); setGroupEdit(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{groupEdit ? "Rename group" : "Add group"}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Group name *</Label>
            <Input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Headache protocols"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setGroupDialogOpen(false); setGroupEdit(null); }}>
              Cancel
            </Button>
            <Button
              onClick={submitGroup}
              disabled={!groupName.trim() || createGroup.isPending || updateGroup.isPending}
              className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
